import InitApi from "api/InitApi";
import ServerErrors from "components/SeverErrors";
import toast from "react-hot-toast";
import { useMutation, useQueryClient } from "react-query";
import { useHistory } from "react-router-dom";
import { reactLocalStorage } from "reactjs-localstorage";
import useTokenStore from "store/tokenStore";

/**
 * Creates one or many tags. Accepts an array of names; each is posted
 * individually so partial success is reported instead of failing the batch.
 */
const useAddTags = () => {
	const queryClient = useQueryClient();
	const { accessToken } = useTokenStore();
	const history = useHistory();

	const save = async (names) => {
		const instance = await InitApi({ accessToken });
		const list = (Array.isArray(names) ? names : [names])
			.map((name) => (typeof name === "string" ? name.trim() : ""))
			.filter(Boolean);

		const results = await Promise.allSettled(
			list.map((name) => instance.post("/book-tags", { name }))
		);

		await queryClient.invalidateQueries("tags");

		const created = results.filter((r) => r.status === "fulfilled").length;
		const failed = results
			.map((r, index) => (r.status === "rejected" ? { name: list[index], error: r.reason } : null))
			.filter(Boolean);

		if (created === 0 && failed.length > 0) {
			throw failed[0].error;
		}

		return { created, failed, total: list.length };
	};

	return useMutation(save, {
		onSuccess: ({ created, failed }) => {
			if (failed.length === 0) {
				toast.success(created === 1 ? "Tag created successfully" : `${created} tags created successfully`);
				return;
			}
			toast(`${created} added; ${failed.length} could not be added. Review the remaining names and try again.`, {
				duration: 6000,
			});
		},
		onError: async (error) => {
			toast.error(
				<ServerErrors
					errorMessage={error?.response?.data?.message}
					errors={error?.response?.data?.data}
				/>,
				{ className: "max-w-max" }
			);

			if (error?.response?.data?.message === "Unauthenticated.") {
				reactLocalStorage.clear();
				history.push("/");
			}
		},
	});
};

export default useAddTags;
